import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect, useRouter } from "expo-router";
import { useCallback, useState } from "react";
import {
    ActivityIndicator,
    Modal,
    Pressable,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import {
    ComplianceCircleIcon,
    ComplianceItemCard
} from "@/components/compliance/ComplianceItemCard";
import { useAuth } from "@/lib/auth";
import {
    docStatusToType,
    fetchRnCompliance,
    type ComplianceRequirement,
    type RnComplianceView
} from "@/lib/compliance-api";
import { colors, fonts, gradients, radius, spacing, typography } from "@/lib/theme";

// The screen only surfaces items that need the RN's attention. Fully
// compliant items with >30 days until expiry are hidden — they pop back in
// under "Expiring soon" once they cross the warn window, and re-appear in
// the relevant section if they slip to missing / not_compliant. Keeps the
// list short so the RN sees what to renew, not a stack of green checkmarks.
const SECTIONS: { key: keyof RnComplianceView["groups"]; title: string }[] = [
    { key: "issues", title: "Items with issues" },
    { key: "expiring", title: "Expiring soon" },
    { key: "pending", title: "Pending approval" }
];

function RequirementSection({
    title,
    requirements,
    onOpen
}: {
    title: string;
    requirements: ComplianceRequirement[];
    onOpen: (req: ComplianceRequirement) => void;
}) {
    if (requirements.length === 0) {
        return null;
    }
    return (
        <View style={styles.section}>
            <Text style={styles.sectionTitle}>{title}</Text>
            {requirements.map((req) => (
                <ComplianceItemCard
                    key={`${req.kind}-${req.itemId ?? req.groupId}`}
                    name={req.name}
                    subtitle={req.subtitle}
                    statusType={req.statusType}
                    rejectionReason={req.rejectionReason}
                    onPress={
                        req.docId != null ||
                        req.training != null ||
                        (req.kind === "group" && req.members.length > 0)
                            ? () => onOpen(req)
                            : undefined
                    }
                />
            ))}
        </View>
    );
}

export default function MyComplianceScreen() {
    const router = useRouter();
    const { user } = useAuth();
    const rnId = user?.rnId ? String(user.rnId) : "";

    const [view, setView] = useState<RnComplianceView | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // OR-group member chooser (which option to satisfy: vaccine vs declination…).
    const [chooser, setChooser] = useState<ComplianceRequirement | null>(null);

    const load = useCallback(async () => {
        if (!rnId) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setView(await fetchRnCompliance(rnId));
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to load compliance");
        } finally {
            setLoading(false);
        }
    }, [rnId]);

    useFocusEffect(
        useCallback(() => {
            load();
        }, [load])
    );

    function openRequirement(req: ComplianceRequirement) {
        // OR-group: let the nurse pick which option to satisfy (each member opens
        // its own detail — form or upload, per the member's type).
        if (req.kind === "group" && req.members.length > 0) {
            setChooser(req);
            return;
        }
        // A course item is renewed by taking the course, not by uploading a
        // document: open the training itself. The screen reloads on focus, so
        // coming back after finishing shows the renewed item straight away.
        if (req.training) {
            router.push(`/training/${req.training.bundleSlug}`);
            return;
        }
        if (req.docId != null) {
            router.push(`/compliance/${req.docId}`);
        }
    }

    function openMember(member: ComplianceRequirement["members"][number]) {
        setChooser(null);
        if (member.docId != null) {
            router.push(`/compliance/${member.docId}`);
        }
    }

    const totalCount = view
        ? view.groups.issues.length +
          view.groups.expiring.length +
          view.groups.pending.length +
          view.groups.compliant.length
        : 0;
    const cleanCount = view ? view.groups.compliant.length : 0;

    return (
        <SafeAreaView edges={["top", "bottom"]} style={styles.container}>
            <View style={styles.topBar}>
                <TouchableOpacity
                    style={styles.backArrow}
                    hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                    onPress={() => {
                        if (router.canGoBack()) {
                            router.back();
                        } else {
                            router.replace("/(tabs)");
                        }
                    }}
                >
                    <MaterialCommunityIcons
                        name="chevron-left"
                        size={28}
                        color={colors.textPrimary}
                    />
                </TouchableOpacity>
            </View>

            <ScrollView contentContainerStyle={styles.scroll}>
                <Text style={styles.header}>Compliance</Text>
                <Text style={styles.subheader}>
                    Keep your credentials up to date to stay eligible for shifts.
                </Text>

                {loading && (
                    <View style={styles.center}>
                        <ActivityIndicator size="large" color={colors.teal400} />
                    </View>
                )}

                {error && !loading && (
                    <View style={styles.center}>
                        <MaterialCommunityIcons
                            name="alert-circle-outline"
                            size={44}
                            color={colors.error500}
                        />
                        <Text style={styles.errorText}>{error}</Text>
                        <TouchableOpacity onPress={load}>
                            <LinearGradient
                                colors={[...gradients.brand]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 0 }}
                                style={styles.retryBtn}
                            >
                                <Text style={styles.retryText}>Retry</Text>
                            </LinearGradient>
                        </TouchableOpacity>
                    </View>
                )}

                {view && !loading && !error && totalCount === 0 && (
                    <View style={styles.center}>
                        <MaterialCommunityIcons
                            name="check-decagram"
                            size={56}
                            color={colors.success500}
                        />
                        <Text style={styles.emptyTitle}>You&apos;re all set</Text>
                        <Text style={styles.emptyBody}>No compliance documents on file yet.</Text>
                    </View>
                )}

                {view &&
                    !loading &&
                    !error &&
                    totalCount > 0 &&
                    view.groups.issues.length === 0 &&
                    view.groups.expiring.length === 0 &&
                    view.groups.pending.length === 0 && (
                        <View style={styles.allClear}>
                            <MaterialCommunityIcons
                                name="shield-check"
                                size={20}
                                color={colors.success600}
                            />
                            <Text style={styles.allClearText}>
                                All {cleanCount} documents are compliant.
                            </Text>
                        </View>
                    )}

                {view &&
                    !loading &&
                    !error &&
                    SECTIONS.map((sec) => (
                        <RequirementSection
                            key={sec.key}
                            title={sec.title}
                            requirements={view.groups[sec.key]}
                            onOpen={openRequirement}
                        />
                    ))}
            </ScrollView>

            {/* OR-group member chooser — pick which option to satisfy. */}
            <Modal
                visible={chooser != null}
                transparent
                animationType="slide"
                onRequestClose={() => setChooser(null)}
            >
                <Pressable style={styles.sheetBackdrop} onPress={() => setChooser(null)}>
                    <Pressable style={styles.sheet} onPress={(e) => e.stopPropagation()}>
                        <View style={styles.sheetHandle} />
                        <Text style={styles.sheetTitle}>{chooser?.name}</Text>
                        <Text style={styles.sheetSubtitle}>
                            Choose one option to satisfy this requirement.
                        </Text>
                        {(chooser?.members ?? []).map((m) => {
                            // Factor expiry into the member's status so a
                            // satisfying-but-expiring option reads amber, not a
                            // reassuring green "Provided".
                            const type = docStatusToType(m.status, { expiryDate: m.expiryDate });
                            const done = m.status === "compliant" || m.status === "resolved";
                            const action =
                                m.itemType === "compliance_form"
                                    ? "Fill out the form"
                                    : "Upload a document";
                            const label = done
                                ? type === "expiringSoon"
                                    ? "Provided · expiring soon"
                                    : "Provided"
                                : action;
                            return (
                                <Pressable
                                    key={m.itemId}
                                    style={styles.memberRow}
                                    onPress={() => openMember(m)}
                                    disabled={m.docId == null}
                                >
                                    <ComplianceCircleIcon statusType={type} size={36} />
                                    <View style={styles.memberBody}>
                                        <Text style={styles.memberName}>{m.name}</Text>
                                        <Text style={styles.memberAction}>{label}</Text>
                                    </View>
                                    <MaterialCommunityIcons
                                        name="chevron-right"
                                        size={22}
                                        color={colors.textTertiary}
                                    />
                                </Pressable>
                            );
                        })}
                    </Pressable>
                </Pressable>
            </Modal>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    topBar: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: spacing.sm,
        paddingTop: spacing.xs
    },
    backArrow: {
        width: 44,
        height: 44,
        alignItems: "center",
        justifyContent: "center"
    },
    scroll: {
        paddingHorizontal: spacing.screenPadding,
        paddingBottom: spacing["3xl"],
        gap: spacing.md
    },
    header: {
        ...typography.display,
        color: colors.textPrimary
    },
    subheader: {
        ...typography.body,
        color: colors.textSecondary,
        marginBottom: spacing.sm
    },
    center: {
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.md,
        paddingVertical: spacing["4xl"]
    },
    errorText: {
        ...typography.body,
        color: colors.error500,
        textAlign: "center"
    },
    retryBtn: {
        paddingHorizontal: spacing.lg,
        paddingVertical: spacing.sm + 2,
        borderRadius: radius.full
    },
    retryText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.surface
    },
    emptyTitle: {
        ...typography.h2,
        color: colors.textPrimary
    },
    emptyBody: {
        ...typography.body,
        color: colors.textSecondary,
        textAlign: "center"
    },
    allClear: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        backgroundColor: colors.success50,
        borderRadius: radius.md,
        padding: spacing.base
    },
    allClearText: {
        ...typography.body,
        color: colors.success600,
        flex: 1
    },
    section: {
        gap: spacing.sm
    },
    sectionTitle: {
        ...typography.subtitle,
        color: colors.textPrimary,
        marginTop: spacing.sm
    },
    sheetBackdrop: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.4)",
        justifyContent: "flex-end"
    },
    sheet: {
        backgroundColor: colors.background,
        borderTopLeftRadius: radius.lg,
        borderTopRightRadius: radius.lg,
        paddingHorizontal: spacing.screenPadding,
        paddingTop: spacing.sm,
        paddingBottom: spacing["2xl"],
        gap: spacing.sm
    },
    sheetHandle: {
        alignSelf: "center",
        width: 40,
        height: 4,
        borderRadius: radius.full,
        backgroundColor: colors.border,
        marginBottom: spacing.sm
    },
    sheetTitle: {
        ...typography.h2,
        color: colors.textPrimary
    },
    sheetSubtitle: {
        ...typography.body,
        color: colors.textSecondary,
        marginBottom: spacing.xs
    },
    memberRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.border,
        padding: spacing.base
    },
    memberBody: {
        flex: 1,
        gap: 2
    },
    memberName: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    memberAction: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    }
});
